/**
 * useLoginLinkState — l'échange du lien « Me connecter » (`/login-link?token=…`) et ce qui le suit.
 *
 * Le portage de `src/auth/LoginLinkScreen.js`, geste pour geste :
 *  - le jeton vient de la route, à défaut de `capturedLoginLinkToken()` (sur le web, il a déjà quitté
 *    la barre d'adresse) ; il part UNE fois (`POST api/v1/login-link`, `{ token, device_name }`) et
 *    est oublié aussitôt : il ne sert qu'une fois ;
 *  - personne n'était connecté à l'ouverture → on entre (`land`) ; le même compte → l'accueil ; un
 *    AUTRE compte → on demande (`pendingSwitch`), et RIEN des comptes du lien n'est rangé avant le
 *    choix : `acceptLoginLink` ne part qu'au moment d'entrer ;
 *  - « Continuer en tant que… » ferme la session ouverte (`LOGOUT_401`), puis entre une fois le
 *    jeton de session parti ; « Rester » rend l'accueil ;
 *  - un lien refusé, ou sans jeton, là où une session existe déjà : on y entre sans un mot
 *    (`landOnSession`), sinon « Ce lien a expiré ».
 *
 * L'écran vit dans toutes les piles : entrer ne le quitte pas tout seul, d'où le `replace("menu")`
 * explicite (le choix du compte, quand c'est lui la destination, a son propre `replace`).
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useNavigation } from "@react-navigation/native"
import * as Device from "expo-device"
import { useDispatch, useSelector } from "react-redux"

import { landOnSession, SESSION_RANK } from "../../../../../../data/auth/crossTabSession"
import { capturedLoginLinkToken, forgetLoginLinkToken } from "../../../../../../data/auth/loginLinkToken"
import { continueAfterLogin, trackLoginSucceeded } from "../../../../../../data/auth/loginSuccess"
import { authConstants } from "../../../../../../data/constants/authConstants"
import { acceptLoginLink, switchAccount } from "../../../../../../data/redux/actions/authTokens"
import { authService } from "../../../../../../data/services/authService"
import { type ApiLoginLinkSuccess } from "../../../../adapters/auth/LoginLink/loginLinkAdapter"
import { landsOnSession, selectSessionRank } from "../../../../view-models/auth/LoginLink/loginLinkViewModel"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"

export type LoginLinkUiState = {
    /** Pas de jeton, ou le serveur l'a refusé (déjà servi, plus de dix minutes). */
    failed: boolean
    /** Le lien est celui d'un autre compte que celui qui est ouvert : la réponse attend le choix. */
    pendingSwitch: ApiLoginLinkSuccess | null
    /** « Continuer » est pressé : on entre dès que la session ouverte est fermée. */
    switchingTo: ApiLoginLinkSuccess | null
}

export type LoginLinkUiAction =
    { type: "FAILED" } | { type: "ASK_SWITCH"; login: ApiLoginLinkSuccess } | { type: "SWITCH" } | { type: "LANDED" }

export function initialLoginLinkUi(token: string | null): LoginLinkUiState {
    return { failed: !token, pendingSwitch: null, switchingTo: null }
}

export function loginLinkUiReducer(state: LoginLinkUiState, action: LoginLinkUiAction): LoginLinkUiState {
    switch (action.type) {
        case "FAILED":
            return { ...state, failed: true }
        case "ASK_SWITCH":
            return { ...state, pendingSwitch: action.login }
        case "SWITCH":
            return { ...state, pendingSwitch: null, switchingTo: state.pendingSwitch }
        case "LANDED":
            return { ...state, switchingTo: null }
        default:
            return state
    }
}

type LoginLinkNavigation = {
    replace: (name: string) => void
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selectCurrentUserId = (state: any): number | null => selectAccount(state)?.id ?? null

export function useLoginLinkState(routeToken: string | null | undefined) {
    const navigation = useNavigation<LoginLinkNavigation>()
    const dispatch = useDispatch()

    const [token] = useState<string | null>(() => routeToken ?? capturedLoginLinkToken() ?? null)
    const [ui, dispatchUi] = useReducer(loginLinkUiReducer, token, initialLoginLinkUi)

    const currentUserId = useSelector(selectCurrentUserId)
    const rank = useSelector(selectSessionRank)
    const sessionOpen = rank === SESSION_RANK.OPEN
    // Qui était connecté quand le lien s'est ouvert.
    const loggedInUserIdRef = useRef(sessionOpen ? currentUserId : null)

    const land = useCallback(
        (login: ApiLoginLinkSuccess) => {
            dispatch(acceptLoginLink(login) as never)
            trackLoginSucceeded(login.method, login, { designSystem: true })
            continueAfterLogin(login, {
                navigation,
                switchAccount: (tokenData: unknown) => {
                    dispatch(switchAccount(tokenData) as never)
                    navigation.replace("menu")
                },
            })
        },
        [dispatch, navigation]
    )

    const { start } = useApiMutation({
        service: authService.redeemLoginLink,
        onSuccess: (data) => {
            const login = (data ?? {}) as ApiLoginLinkSuccess
            if (!loggedInUserIdRef.current) {
                land(login)
                return
            }
            if (loggedInUserIdRef.current === login.user?.id) {
                navigation.replace("menu")
                return
            }
            dispatchUi({ type: "ASK_SWITCH", login })
        },
        onError: () => dispatchUi({ type: "FAILED" }),
    })

    // Le jeton ne sert qu'une fois : une seule requête, même si l'effet se rejoue.
    const submittedRef = useRef(false)
    useEffect(() => {
        if (!token || submittedRef.current) return
        submittedRef.current = true
        forgetLoginLinkToken()
        start({}, { token, device_name: Device.deviceName ?? "WEB" })
    }, [token, start])

    // La bascule : quitter la session ouverte, puis entrer comme une connexion neuve.
    useEffect(() => {
        if (!ui.switchingTo || sessionOpen) return
        land(ui.switchingTo)
        dispatchUi({ type: "LANDED" })
    }, [ui.switchingTo, sessionOpen, land])

    const lands = landsOnSession(ui.failed, rank)
    useEffect(() => {
        if (lands) landOnSession(navigation, rank)
        // Seulement quand le lien bascule vers « refusé avec une session ici », comme le legacy.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lands])

    const onContinueAsLinkUser = useCallback(() => {
        dispatchUi({ type: "SWITCH" })
        dispatch({ type: authConstants.LOGOUT_401 })
    }, [dispatch])

    const onStayAsCurrentUser = useCallback(() => navigation.replace("menu"), [navigation])
    const onRequestNewCode = useCallback(() => navigation.replace("login"), [navigation])

    return {
        failed: ui.failed,
        pendingSwitch: ui.pendingSwitch,
        onContinueAsLinkUser,
        onStayAsCurrentUser,
        onRequestNewCode,
    }
}
